import type { ReactNode } from 'react';
import React from 'react';
import type { Coordinate } from '@modules/professional/type/contact-page-types';
import { Label, useStyles } from '@archipro-design/aria';
import * as S from './ClickToView.style';
import { useTracker } from '@archipro-website/tracker';
import { useRootData } from '@modules/root/hook/use-root-data';
import type { ClickToRevealType } from '../click-to-reveal/ClickToReveal';
import {
    useClickToReveal,
    type UseClickToRevealProps,
} from '../../hook/use-click-to-reveal';

interface Props extends Omit<UseClickToRevealProps, 'revealType'> {
    fulltext: string;
    type: ClickToRevealType;
    clickLabel: string;
    className?: string;
    coordinates?: Coordinate;
    buttonTrackingID: string;
    label: string;
    /**
     * Override the href used for Phone/Mobile tel: links.
     * - `string`    — used as-is (e.g. a sanitised `tel:+6492730040`)
     * - `null`      — renders the phone number as plain text (not clickable)
     * - `undefined` — falls back to the legacy `tel:${fulltext}` behaviour
     */
    telHref?: string | null;
}

const ClickToView: React.FC<Props> = ({
    fulltext,
    type,
    clickLabel,
    className,
    buttonTrackingID,
    coordinates,
    label,
    revealKey,
    revealSubKey,
    telHref,
}: Props) => {
    const { log } = useTracker();
    const { device } = useRootData();

    const isPhoneType = type === 'Phone' || type === 'Mobile';
    const resolvedTelHref =
        telHref !== undefined
            ? telHref
            : isPhoneType
              ? `tel:${fulltext}`
              : null;

    const phoneLink = resolvedTelHref ? (
        <Label
            as="a"
            variant={'02'}
            href={resolvedTelHref}
            onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                log(`${type}Submit`, {
                    url: new URL(window.location.href),
                    label: label,
                });
            }}
        >
            {fulltext}
        </Label>
    ) : (
        <Label variant={'02'}>{fulltext}</Label>
    );

    const showMapLink = type === 'Location' && coordinates?.Latitude;

    const mapHref =
        device.type === 'desktop'
            ? `https://www.google.com/maps/dir/?api=1&destination=${coordinates?.Latitude},${coordinates?.Longitude}&z=9`
            : device.os.isApple
              ? `http://maps.apple.com/?ll=${coordinates?.Latitude},${coordinates?.Longitude}&q=${fulltext}&z=8`
              : device.os.isAndroid
                ? `geo://0,0?q=${coordinates?.Latitude},${coordinates?.Longitude}&z=8`
                : `https://maps.google.com/?q=${coordinates?.Latitude},${coordinates?.Longitude}&z=8`;

    const locationLink = showMapLink ? (
        <Label as="a" href={mapHref} variant={'02'} target="_blank">
            {fulltext}
        </Label>
    ) : (
        <Label>{fulltext}</Label>
    );

    return (
        <TrackingClickToView
            key={fulltext}
            className={`${className ? className : ''}`}
            buttonTrackingID={buttonTrackingID}
            revealKey={revealKey}
            revealType={type}
            revealSubKey={revealSubKey}
        >
            {(clicked: boolean) => {
                if (!clicked)
                    return (
                        <Label styles={{ textDecoration: 'underline' }}>
                            View {clickLabel.toLocaleLowerCase()}
                        </Label>
                    );
                return isPhoneType ? phoneLink : locationLink;
            }}
        </TrackingClickToView>
    );
};

type RenderFunction = (clicked: boolean) => ReactNode;

interface TrackingClickToRevealProps extends UseClickToRevealProps {
    children: RenderFunction;
    className?: string;
    buttonTrackingID?: string;
}

const TrackingClickToView: React.FC<TrackingClickToRevealProps> = ({
    children,
    buttonTrackingID,
    revealKey,
    revealType,
    revealSubKey,
}: TrackingClickToRevealProps) => {
    const { revealed, onRevealClick } = useClickToReveal({
        revealType,
        revealKey,
        revealSubKey,
    });
    const { css } = useStyles({ clicked: revealed });
    return (
        <div
            className={css(S.ClickToRevealContainer)}
            data-button={buttonTrackingID || null}
            onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onRevealClick();
            }}
        >
            {children(revealed)}
        </div>
    );
};

export default ClickToView;
